import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import {
  activeFilterCount,
  BULK_RIGHTS,
  type BulkFormKind,
  bulkRightsQuery,
  CREATE_RIGHT,
  createRightQuery,
  EMPTY_TICKET_FILTERS,
  currentFiltersQuery,
  filtersFromStored,
  ID_FILTER_KEYS,
  removeQuickFilter,
  saveCurrentFilters,
  saveQuickFilter,
  savedFiltersQuery,
  savedKindOf,
  takeOverTicket,
  ticketFilterOptionsQuery,
  ticketPageQuery,
  trashTicket,
  trashTickets,
  type IdFilterKey,
  type SavedFilter,
  type TicketFilters,
  type TicketListName,
} from '../api/easeedesk'
import type { Named } from '../api/shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Modal } from '../ui/Modal'
import { SelectPicker, type SelectPickerOption } from '../ui/SelectPicker'
import { TicketBulkForms } from './TicketBulkForms'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { closedTicketColumns, ticketColumns, type TicketRow } from './columns'
import { TicketCommentForm } from './TicketCommentForm'
import { TicketCreateForm } from './TicketCreateForm'
import { QuickTicketModal } from './QuickTicketModal'
import { chatTicketsQuery } from '../api/chatSupport'
import { ChatSupportPanel, ChatTicketsTable } from './ChatSupportPanel'
import { TicketExpandedRow } from './TicketPage'
import { UnknownOwnerModal } from './UnknownOwnerModal'
import './TicketCreateForm.css'
import './TicketListFilters.css'

/**
 * Listele de tichete - Ticket list si Closed tickets - cu machetele vechi
 * (`easeedesk/tickets/index.php` si `closed_tickets/index.php`): acelasi
 * tabel, aceleasi coloane, aceeasi bara de filtre rapide deasupra si acelasi
 * panou de filtre.
 *
 * CE E AICI, SI CE NU
 * -------------------
 * Citirea (lista, filtrele, tinerea lor minte) si TREI scrieri de pe rand -
 * Take over, Comment, Move to trash - plus selectia pe mai multe randuri cu
 * mutarea lor la cos. Fiecare merge la capul ei nou din Symfony, cu acelasi
 * drept ca in portal.
 *
 * Din meniul "Actions" de deasupra listei, celelalte trei - Merge, Suspend
 * service, Assign to Operator - se vad, dar sunt stinse si spun de ce: ele
 * deschideau in portal cate un formular construit din baza, si acelea nu s-au
 * mutat. Un buton care arata activ si nu face nimic e mai rau decat unul
 * stins care spune unde se face lucrul.
 *
 * SELECTIA nu e tinuta in React, ci in DOM, ca in portal: `TicketsModel`
 * deseneaza `<input class="select-ticket">` chiar in celula, iar actiunile
 * citeau `$('.select-ticket:checked')`. Trecuta prin stare, fiecare bifa ar fi
 * redesenat tabelul si ar fi inchis randul desfasurat de sub degetul omului.
 */

export const TICKET_TABLE_ID: Record<TicketListName, string> = {
  tickets: 'ticketsTable',
  closed_tickets: 'closedTicketsTable',
}

/**
 * Marimile de pagina ale listei ACTIVE.
 *
 * Serverul taie pagina la 50 (`TicketListQuery::ACTIVE_MAX_LENGTH`, ca in
 * `Tickets_summary::getData`), fiindca fiecare rand mai cere cronometre,
 * escaladari si nume de oameni. Meniul vechi oferea tot `[10, 25, 50]`.
 *
 * Cu 100 in meniu, paginarea numara paginile dupa 100 iar serverul da 50:
 * jumatate din lista nu se mai putea deschide de nicaieri. Lista inchisa taie
 * la 5000, deci acolo ramane meniul obisnuit.
 */
export const ACTIVE_LENGTHS = [10, 25, 50]

/** Ordinea implicita a fiecarei liste, ca in `order: [[1, 'desc']]` din portal. */
const DEFAULT_ORDER: Record<TicketListName, number> = {
  tickets: 1,
  closed_tickets: 1,
}

const ADVANCED_OPTIONS: Named[] = [
  { id: 'unassigned' as unknown as number, name: 'Unassigned' },
  { id: 'my_tickets' as unknown as number, name: 'My tickets' },
  { id: 'created_by_me' as unknown as number, name: 'Created by me' },
  { id: 'planned' as unknown as number, name: 'Planned tickets' },
]

/** Cele patru tipuri de escaladare, cum le numeste `ticketStatusName`. */
const ESCALATION_OPTIONS: Named[] = [
  { id: 1, name: 'Internal escalated' },
  { id: 2, name: 'Customer escalated' },
  { id: 3, name: 'External escalated' },
  { id: 4, name: 'Startechteam escalated' },
]

/**
 * O caseta de filtru cu mai multe valori: `SelectPicker` (bootstrap-select cu
 * liveSearch), acelasi ca in restul v4 - cautare in lista, bife, si
 * "Select All" / "Deselect All" deasupra, care lucreaza pe ce a ramas dupa
 * cautare. Inainte era un `<select multiple>` gol de stil, unde a alege doua
 * valori cerea Ctrl+clic si nu se putea cauta.
 *
 * Serverul primeste lista ca pana acum (`status[]=1&status[]=2`), iar
 * `TicketListQuery` o leaga ca `IN (...)`; o valoare singura merge la fel.
 */
function MultiSelect({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string
  name: string
  options: Named[]
  value: string[]
  onChange(next: string[]): void
}) {
  return (
    <div className="form-group">
      <label htmlFor={`filter-${name}`}>{label}</label>
      <SelectPicker
        id={`filter-${name}`}
        className="form-control filter-select"
        name={name}
        title="All"
        multiple
        actionsBox
        selectedTextFormat="count > 2"
        size={10}
        options={toPickerOptions(options)}
        value={value}
        onChange={onChange}
      />
    </div>
  )
}

/**
 * Caseta cu O SINGURA valoare (Advanced, Taken by Adam Intop): tot
 * `SelectPicker`, cu cautare, fara bife. Primul rand, "-Select...", e cel care
 * scoate filtrul - la fel ca optiunea goala din macheta.
 */
function SingleSelect({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string
  name: string
  options: SelectPickerOption[]
  value: string
  onChange(next: string): void
}) {
  return (
    <div className="form-group">
      <label htmlFor={`filter-${name}`}>{label}</label>
      <SelectPicker
        id={`filter-${name}`}
        className="form-control filter-select"
        name={name}
        title="-Select..."
        options={[{ value: '', label: '-Select...' }, ...options]}
        value={value}
        onChange={onChange}
      />
    </div>
  )
}

function toPickerOptions(options: Named[]): SelectPickerOption[] {
  return options.map((o) => ({ value: String(o.id), label: o.name }))
}

/**
 * Panoul de filtre - `easeedesk/tickets/filterSidebar.php`.
 *
 * Exportat ca sa poata fi probat singur: in pagina el se deseneaza inauntrul
 * lui `DataTable`, intr-un portal catre bara de sus, si bara aia nu exista in
 * afara scheletului.
 */
export function FiltersPanel({
  list,
  applied,
  onApply,
  close,
  activeQuickId = null,
  onPickSaved = () => {},
  onSavedRemoved = () => {},
}: {
  list: TicketListName
  applied: TicketFilters
  onApply(next: TicketFilters): void
  close(): void
  /** Filtrul salvat aplicat acum, daca e unul. */
  activeQuickId?: number | null
  /** Ales din "Saved filters": pagina il aplica, panoul ramane deschis. */
  onPickSaved?(filter: SavedFilter): void
  /** Sters din "Saved filters". */
  onSavedRemoved?(id: number): void
}) {
  const options = useQuery(ticketFilterOptionsQuery)
  const [draft, setDraft] = useState<TicketFilters>(applied)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const client = useQueryClient()
  const kind = savedKindOf(list)

  useEffect(() => setDraft(applied), [applied])

  const o = options.data
  const set = (key: IdFilterKey) => (next: string[]) => setDraft((d) => ({ ...d, [key]: next }))

  const save = useMutation({
    mutationFn: () => saveQuickFilter(name.trim(), kind, draft),
    onSuccess: () => {
      setNaming(false)
      setName('')
      void client.invalidateQueries({ queryKey: ['easeedesk', 'quick-filters', kind] })
    },
  })

  return (
    <>
      <header className="panel-heading sm bg-default">
        <h4>
          <i className="fas fa-search-plus"></i> <strong>Apply</strong> filters
        </h4>
      </header>
      <div className="panel-body">
        <form
          id="easeedesk-filters"
          className="form-horizontal labelcustomize tableFilters tk-filters-compact"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            onApply(draft)
          }}
          onReset={(e) => {
            e.preventDefault()
            setDraft(EMPTY_TICKET_FILTERS)
          }}
        >
          <SavedFiltersRow
            list={list}
            activeId={activeQuickId}
            onPick={(f) => {
              setDraft(filtersFromSaved(f))
              onPickSaved(f)
            }}
            onRemoved={onSavedRemoved}
          />

          {/*
            O singura grila, fara coloane Bootstrap: casetele curg in ordinea
            filtrelor, cate incap pe rand (sapte pe un ecran de 1860px, doua pe
            telefon), fara gaurile pe care le lasau cele trei coloane cand
            Application statea singura. Vezi TicketListFilters.css.
          */}
          <div className="tk-filters-grid">
            <MultiSelect label="Type" name="type" options={o?.types ?? []} value={draft.type} onChange={set('type')} />
            <MultiSelect label="Company" name="company" options={o?.companies ?? []} value={draft.company} onChange={set('company')} />
            <MultiSelect label="Status" name="status" options={o?.statuses ?? []} value={draft.status} onChange={set('status')} />
            <MultiSelect label="Escalation" name="escalation_type" options={ESCALATION_OPTIONS} value={draft.escalation_type} onChange={set('escalation_type')} />
            <MultiSelect label="Priority" name="priority" options={o?.priorities ?? []} value={draft.priority} onChange={set('priority')} />
            <MultiSelect label="Category" name="category" options={o?.categories ?? []} value={draft.category} onChange={set('category')} />
            <MultiSelect label="Subcategory" name="subcategory" options={o?.subcategories ?? []} value={draft.subcategory} onChange={set('subcategory')} />
            <MultiSelect label="Assigned Group" name="group" options={o?.groups ?? []} value={draft.group} onChange={set('group')} />
            <MultiSelect label="Assignee" name="assignee" options={o?.assignees ?? []} value={draft.assignee} onChange={set('assignee')} />
            {/* Aplicatia de business a aparatelor tichetului; fara aplicatii, fara caseta. */}
            {(o?.applications?.length ?? 0) > 0 && (
              <MultiSelect label="Application" name="application" options={o?.applications ?? []} value={draft.application} onChange={set('application')} />
            )}
            <SingleSelect
              label="Advanced"
              name="advanced"
              options={toPickerOptions(ADVANCED_OPTIONS)}
              value={draft.advanced}
              onChange={(next) => setDraft((d) => ({ ...d, advanced: next }))}
            />
            {/*
              "Taken by Adam Intop", ultimul sertar din bara veche.
              Filtrul pleca deja spre server, se numara in insigna de langa
              palnie si se aducea inapoi din Redis - numai casuta lipsea, deci
              cine il avea pus nu-l putea scoate, si cine nu-l avea nu-l putea
              pune. Aceleasi doua optiuni ca in macheta: "-Select..." si "Yes".
            */}
            <SingleSelect
              label="Taken by Adam Intop"
              name="taken_by_adam"
              options={[{ value: '1', label: 'Yes' }]}
              value={draft.taken_by_adam ? '1' : ''}
              onChange={(next) => setDraft((d) => ({ ...d, taken_by_adam: next === '1' }))}
            />
            <div className="form-group">
              <label htmlFor="filter-from">Created from</label>
              <input
                id="filter-from"
                type="date"
                className="form-control input-sm"
                name="filterFrom"
                value={draft.filterFrom}
                onChange={(e) => setDraft((d) => ({ ...d, filterFrom: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="filter-to">Created to</label>
              <input
                id="filter-to"
                type="date"
                className="form-control input-sm"
                name="filterTo"
                value={draft.filterTo}
                onChange={(e) => setDraft((d) => ({ ...d, filterTo: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-group filters-actions">
            <button type="submit" className="btn btn-sm btn-primary apply-filters">
              <i className="fa fa-check" aria-hidden="true"></i> Apply
            </button>{' '}
            <button type="reset" className="btn btn-sm btn-inverse">
              Reset
            </button>{' '}
            <button type="button" className="btn btn-sm btn-danger new-quick-filter" onClick={() => setNaming(true)}>
              <i className="fas fa-plus" aria-hidden="true"></i> Save Quick Filter
            </button>{' '}
            <button type="button" className="btn btn-sm closeFilters" onClick={close}>
              Cancel
            </button>
          </div>

          {naming && (
            <div className="form-group new-filter-name">
              <label htmlFor="quick-filter-name">Quick filter name</label>
              <input
                className="form-control"
                type="text"
                id="quick-filter-name"
                name="filter-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-success save-quick-filter"
                disabled={name.trim() === '' || save.isPending}
                onClick={() => save.mutate()}
              >
                Save
              </button>{' '}
              <button type="button" className="btn btn-sm btn-default cancel-quick-filter" onClick={() => setNaming(false)}>
                Cancel
              </button>
              {save.isError && <p className="text-danger">{(save.error as Error).message}</p>}
            </div>
          )}
        </form>
      </div>
    </>
  )
}

/**
 * Filtrele salvate (`it_easeedesk_quick_filters`) - ale grupului si ale omului -
 * stau NUMAI in panoul de filtre, pe primul rand: o lista cu cautare, si
 * langa ea Delete pentru cele proprii (acelasi `quick-filters/remove` pe care
 * il chema x-ul din bara veche). Ales, filtrul isi pune valorile in casetele
 * de dedesubt - omul vede ce face si poate schimba - si se aplica, cum facea
 * clicul pe eticheta din bara de deasupra tabelului.
 *
 * Rename nu exista: serverul n-are cap pentru el (EaseedeskFiltersController
 * stie numai sa listeze, sa salveze si sa stearga).
 */
function SavedFiltersRow({
  list,
  activeId,
  onPick,
  onRemoved,
}: {
  list: TicketListName
  activeId: number | null
  onPick(filter: SavedFilter): void
  onRemoved(id: number): void
}) {
  const kind = savedKindOf(list)
  const saved = useQuery(savedFiltersQuery(kind))
  const client = useQueryClient()
  const [picked, setPicked] = useState(activeId === null ? '' : String(activeId))

  useEffect(() => setPicked(activeId === null ? '' : String(activeId)), [activeId])

  const filters = Array.isArray(saved.data) ? saved.data : []
  const current = filters.find((f) => String(f.ID) === picked) ?? null

  const drop = useMutation({
    mutationFn: (id: number) => removeQuickFilter(id),
    onSuccess: (_r, id) => {
      setPicked('')
      onRemoved(id)
      void client.invalidateQueries({ queryKey: ['easeedesk', 'quick-filters', kind] })
    },
  })

  if (filters.length === 0) return null

  return (
    <div className="tk-saved-filters">
      <div className="form-group">
        <label htmlFor="filter-saved">Saved filters</label>
        <SelectPicker
          id="filter-saved"
          className="form-control filter-select saved-filter-select"
          name="saved_filter"
          title="-Select..."
          size={10}
          options={[
            { value: '', label: '-Select...' },
            ...filters.map((f) => ({ value: String(f.ID), label: f.filter_name, subtext: f.filter_type === 'default' ? 'group' : undefined })),
          ]}
          value={picked}
          onChange={(next) => {
            setPicked(next)
            const f = filters.find((x) => String(x.ID) === next)
            if (f) onPick(f)
          }}
        />
      </div>
      {current?.filter_type === 'custom' && (
        <button
          type="button"
          className="btn btn-sm btn-default remove-filter"
          title={`Delete ${current.filter_name}`}
          disabled={drop.isPending}
          onClick={() => drop.mutate(current.ID)}
        >
          <i className="fas fa-trash" aria-hidden="true"></i> Delete
        </button>
      )}
    </div>
  )
}

/**
 * Deasupra tabelului ramane numai numele filtrului salvat aplicat, ca text,
 * cu un x care il scoate. Fara filtru salvat aplicat, nimic.
 */
function ActiveSavedFilter({
  list,
  activeId,
  onClear,
}: {
  list: TicketListName
  activeId: number | null
  onClear(): void
}) {
  const saved = useQuery(savedFiltersQuery(savedKindOf(list)))
  const filters = Array.isArray(saved.data) ? saved.data : []
  const active = activeId === null ? undefined : filters.find((f) => f.ID === activeId)
  if (!active) return null

  return (
    <div className="quickFilters active-saved-filter">
      Filter: <strong>{active.filter_name}</strong>{' '}
      <button type="button" className="btn btn-xs btn-link clear-saved-filter" title="Clear filter" onClick={onClear}>
        <i className="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  )
}

/** Un filtru salvat, adus in forma cu care lucreaza pagina. */
export function filtersFromSaved(filter: SavedFilter): TicketFilters {
  const out: TicketFilters = { ...EMPTY_TICKET_FILTERS }
  const stored = filter.filter ?? {}

  for (const key of ID_FILTER_KEYS) {
    const value = stored[key]
    if (Array.isArray(value)) out[key] = value.map((v) => String(v)).filter((v) => v !== '')
    else if (typeof value === 'string' && value !== '') out[key] = [value]
  }
  if (typeof stored.advanced === 'string') out.advanced = stored.advanced
  if (typeof stored.filterFrom === 'string') out.filterFrom = stored.filterFrom
  if (typeof stored.filterTo === 'string') out.filterTo = stored.filterTo
  out.taken_by_adam = stored.taken_by_adam === '1' || stored.taken_by_adam === 1 || stored.taken_by_adam === true

  return out
}

/**
 * Meniul "Actions" - `ButtonsActions.multipleActionsBtn`, asezat cum il aseaza
 * `list_chrome.js` (`foldExportsIntoActions`): o bara plutitoare, jos, pe
 * mijloc, peste continut (`.tk-actions-bar`, foaia e in list_chrome.css).
 *
 * Nu in bara de sus. In portal butonul statea in randul de deasupra tabelului,
 * rand pe care list_chrome l-a strans - deci era ascuns, si degeaba se
 * aprindea la selectie. Bara apare cand bifezi ceva (`body.tk-actions-open`)
 * si dispare cand renunti, cu "N selected" langa buton; randurile de sub ea
 * nu se misca. Sta in `body`, nu in pagina: sub un parinte cu `transform`,
 * un element fix nu se mai raporteaza la fereastra. `dropup`: bara e jos,
 * deci meniul se deschide in sus.
 *
 * Aceleasi patru intrari, in aceeasi ordine, fiecare aratata numai daca rolul
 * are dreptul ei; butonul e stins cat timp nu e bifat niciun rand, exact ca
 * `EdMultiActions.enableDisableMultiActions`. Primele trei deschid cate o
 * fereastra (`TicketBulkForms`), ca `data-target="#mergeModal"` si fratii lui.
 */
function BulkActions({
  count,
  onTrash,
  onOpen,
}: {
  count: number
  onTrash(): void
  onOpen(kind: BulkFormKind): void
}) {
  const rights = useQuery(bulkRightsQuery)
  const [open, setOpen] = useState(false)
  const can = rights.data?.can ?? {}

  const shown = [can[BULK_RIGHTS.merge], can[BULK_RIGHTS.suspend], can[BULK_RIGHTS.assign], can[BULK_RIGHTS.trash]]
  const visible = shown.some(Boolean)

  /* Clasa de pe body e cea pe care o citeste foaia; se scoate la plecare,
     ca sa nu ramana o bara aprinsa pe pagina urmatoare. */
  useEffect(() => {
    document.body.classList.toggle('tk-actions-open', visible && count > 0)
    return () => document.body.classList.remove('tk-actions-open')
  }, [visible, count])

  /* Meniul se inchide cand se goleste selectia: bara pleaca, nu poate ramane
     un meniu deschis atarnat de nimic. */
  useEffect(() => {
    if (count === 0) setOpen(false)
  }, [count])

  if (!visible) return null

  const open3 = (kind: BulkFormKind) => (ev: MouseEvent) => {
    ev.preventDefault()
    setOpen(false)
    onOpen(kind)
  }

  return createPortal(
    <div className="tk-actions-bar">
      <span className="tk-actions-count">{count} selected</span>
      <div className={`btn-group dropup${open ? ' open' : ''}`}>
        <button
          type="button"
          className="btn btn-default dropdown-toggle multi-actions"
          disabled={count === 0}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          Actions <span className="caret"></span>
        </button>
        {open && (
          <ul className="dropdown-menu" style={{ display: 'block' }}>
            {can[BULK_RIGHTS.merge] && (
              <li>
                <a href="#" className="merge-tickets" onClick={open3('merge')}>
                  <i className="fa fa-share-alt fa-flip-horizontal" aria-hidden="true"></i> Merge
                </a>
              </li>
            )}
            {can[BULK_RIGHTS.suspend] && (
              <li>
                <a href="#" className="suspend-service" onClick={open3('suspend')}>
                  <i className="far fa-calendar-times fa-flip-horizontal" aria-hidden="true"></i> Suspend service
                </a>
              </li>
            )}
            {can[BULK_RIGHTS.assign] && (
              <>
                <li>
                  <a href="#" className="assign-operator" onClick={open3('assign')}>
                    <i className="fa fa-user-plus" aria-hidden="true"></i> Assign to Operator
                  </a>
                </li>
                <li role="separator" className="divider"></li>
              </>
            )}
            {can[BULK_RIGHTS.trash] && (
              <li>
                <a
                  href="#"
                  className="remove-tickets"
                  onClick={(ev) => {
                    ev.preventDefault()
                    setOpen(false)
                    onTrash()
                  }}
                >
                  <i className="fas fa-trash" aria-hidden="true"></i> Move to trash
                </a>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  )
}

export function TicketListPage({ list }: { list: TicketListName }) {
  const id = TICKET_TABLE_ID[list]
  const [state, setState] = useTableState(id, {
    start: 0,
    length: 25,
    order: { column: DEFAULT_ORDER[list], dir: 'desc' },
    search: '',
  })
  const [filters, setFilters] = useState<TicketFilters>(EMPTY_TICKET_FILTERS)
  const [quickId, setQuickId] = useState<number | null>(null)

  const columns = useMemo(() => (list === 'closed_tickets' ? closedTicketColumns() : ticketColumns()), [list])

  /* Filtrele tinute minte se pun O SINGURA DATA, la deschidere - ca
     `jsGlobals.UserTicketFilters` in portal. Dupa aia hotaraste omul. */
  const stored = useQuery(currentFiltersQuery)
  const [restored, setRestored] = useState(false)
  useEffect(() => {
    if (restored || !stored.isSuccess) return
    setFilters(filtersFromStored(stored.data))
    setRestored(true)
  }, [restored, stored.isSuccess, stored.data])

  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[1]
  const query = useQuery({
    ...ticketPageQuery({
      list,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
      filters,
    }),
    placeholderData: keepPreviousData,
    enabled: restored || stored.isError,
  })

  /*
   * Randurile se tin minte pana se schimba raspunsul.
   *
   * `query.data?.data ?? []` face un tablou NOU la fiecare redesenare, iar
   * `DataTable` inchide randul desfasurat cand se schimba randurile - deci
   * fiecare bifa il inchidea, si numarul bifelor de mai jos se punea la loc pe
   * zero in aceeasi clipa in care il cresteam.
   */
  const fetched = useMemo(() => (query.data?.data ?? []) as TicketRow[], [query.data])

  /*
   * RANDUL DESFASURAT ramane deschis pe tichet pana il inchide omul.
   *
   * Fiecare scriere facuta din randul deschis (Take over, avansarea de stadiu,
   * prioritatea, comentariul, escaladarea) reciteste si lista
   * (`['easeedesk', 'table']`), ca randul sa-si ia starea noua. Tabelul
   * inchidea randul desfasurat la ORICE raspuns nou, deci omul era scos din
   * tichet dupa fiecare buton si trebuia sa-l redeschida.
   *
   * Mai rau: dupa Take over pe un filtru "neasignate", sau dupa o schimbare de
   * stare pe o lista filtrata dupa stare, tichetul nu mai e in raspuns deloc.
   * Atunci randul lui se tine pe loc, cu ultimele valori citite, pana il
   * inchide omul sau pana schimba el vederea (pagina, sortare, cautare,
   * filtre, Refresh). Fisa de dedesubt se reciteste oricum singura
   * (`ticketQuery`), deci ce vede omul in tichet e la zi.
   */
  const [expanded, setExpanded] = useState<{ rid: string; row: TicketRow; at: number } | null>(null)
  const rows = useMemo(() => {
    if (expanded === null) return fetched
    if (fetched.some((r) => String(r.DT_RowId) === expanded.rid)) return fetched
    const kept = [...fetched]
    kept.splice(Math.min(expanded.at, kept.length), 0, expanded.row)
    return kept
  }, [fetched, expanded])

  /* Cat timp tichetul e in raspuns, randul tinut minte se tine la zi. */
  useEffect(() => {
    setExpanded((was) => {
      if (was === null) return was
      const at = fetched.findIndex((r) => String(r.DT_RowId) === was.rid)
      return at < 0 || fetched[at] === was.row ? was : { rid: was.rid, row: fetched[at], at }
    })
  }, [fetched])

  /* O vedere noua (pagina, marime, sortare, cautare, filtre) inchide randul,
     ca la DataTables: acolo hotaraste omul ce vrea sa vada. */
  const view = JSON.stringify([list, state.start, state.length, state.order, state.search, filters])
  useEffect(() => setExpanded(null), [view])

  const openRow = useCallback(
    (rid: string | null) => {
      if (rid === null) {
        setExpanded(null)
        return
      }
      const at = rows.findIndex((r) => String(r.DT_RowId) === rid)
      if (at >= 0) setExpanded({ rid, row: rows[at], at })
    },
    [rows],
  )

  /* Refresh si Retry sunt o reincarcare ceruta de om: randul se inchide. */
  const refresh = useCallback(() => {
    setExpanded(null)
    void query.refetch()
  }, [query])

  /*
   * ECRANUL DE SUPORT PRIN CHAT (`tickets/index.php`, ramura
   * `$_SESSION['has_quick_chat_support'] == 1`): deasupra listei, tabelul
   * discutiilor mele (`#chatTicketsTable`, `easeedesk/chatTicketsData`), iar in
   * dreapta panoul discutiei alese (`.ticket-page-chat`, `quickChatSupport/{id}`).
   * Serverul spune daca omul are steagul (`enabled`); portalul reincarca
   * tabelul la 15 secunde, si tot asa aici.
   */
  const chat = useQuery({ ...chatTicketsQuery, enabled: list === 'tickets', refetchInterval: 15000 })
  const chatOn = list === 'tickets' && chat.data?.enabled === true
  const [chatSelected, setChatSelected] = useState<number | null>(null)

  const apply = (next: TicketFilters, quick: number | null) => {
    setFilters(next)
    setQuickId(quick)
    setState({ start: 0 })
    /* Se tin minte, ca lista sa se deschida maine la fel. Daca nu se poate
       scrie, lista merge oricum: e o comoditate, nu o conditie. */
    void saveCurrentFilters(next).catch(() => undefined)
  }

  /* ------------------------------------------------------------------ */
  /*  SCRIERILE de pe rand si de pe selectie                             */
  /* ------------------------------------------------------------------ */

  const host = useRef<HTMLDivElement>(null)
  /** Tichetele bifate. Un `ref`, nu stare: vezi mai jos de ce. */
  const picked = useRef<Set<number>>(new Set())
  const [checked, setChecked] = useState(0)
  /* Fereastra deschisa din meniul "Actions", cu tichetele bifate in clipa aia. */
  const [bulk, setBulk] = useState<{ kind: BulkFormKind; ids: number[] } | null>(null)
  const [commentOn, setCommentOn] = useState<number | null>(null)
  /* Adresa necunoscuta apasata in coloana "Affected" (`.quick-sidebar-unknown`):
     in portal deschidea panoul "Create owner" din sertarul din dreapta. */
  const [unknownOn, setUnknownOn] = useState<number | null>(null)

  /*
   * "New ticket" - butonul din antetul listei (`createTicketBtn` din
   * `easeedesk.js`, pus in bara tabelului), desenat DOAR sub
   * `easeedesk.tickets.create`, ca `#createTicketModal` in `tickets/index.php`.
   * Dreptul il spune serverul (`/me?can=`), nu-l ghiceste ecranul.
   */
  const createRight = useQuery(createRightQuery)
  const canCreate = list === 'tickets' && createRight.data?.can[CREATE_RIGHT] === true
  const [creating, setCreating] = useState(false)
  const [quick, setQuick] = useState(false)
  /** Tichetul abia deschis: randul lui se evidentiaza cand lista se intoarce. */
  const [born, setBorn] = useState<number | null>(null)

  useEffect(() => {
    if (born === null) return
    const row = host.current?.querySelector<HTMLTableRowElement>(`tr#row_${born}`)
    if (!row) return
    row.classList.add('tk-row-new')
    const timer = setTimeout(() => row.classList.remove('tk-row-new'), 6000)
    return () => clearTimeout(timer)
  }, [born, rows])

  const boxes = useCallback(
    (): HTMLInputElement[] => [...(host.current?.querySelectorAll<HTMLInputElement>('input.select-ticket') ?? [])],
    [],
  )

  /* Randurile noi vin fara bife - la fel le pierdea si DataTables la fiecare
     redesenare (pagina, sortare, Refresh). */
  useEffect(() => {
    picked.current.clear()
    setChecked(0)
  }, [rows])

  /*
   * Bifele se pun inapoi in DOM dupa fiecare redesenare.
   *
   * Casuta e HTML scris de coloana si pus cu `dangerouslySetInnerHTML`, deci
   * React nu-i cunoaste starea: cand isi reface celula, casuta se intoarce
   * nebifata si selectia omului dispare fara ca nimeni sa fi apasat ceva.
   * Adevarul e in `picked`; DOM-ul doar il arata.
   */
  useEffect(() => {
    for (const box of boxes()) box.checked = picked.current.has(Number(box.dataset.id))
  })

  const reload = useCallback(() => {
    picked.current.clear()
    setChecked(0)
    void query.refetch()
  }, [query])

  const takeOver = useCallback(
    function ask(ticketId: number, force = false): void {
      takeOverTicket(ticketId, force)
        .then((answer) => {
          if (answer.confirm === 1) {
            /* Tichetul are deja operator. In portal, butonul rapid din lista
               arunca raspunsul asta si nu se intampla nimic; intrebarea era pe
               alt ecran (fila Takeover a fisei), cu acelasi `force`. */
            if (window.confirm('This ticket is already assigned to someone else. Take it over?')) {
              ask(ticketId, true)
            }
            return
          }
          reload()
          toast(`Ticket #${ticketId} is yours.`, 'Success', 'success')
        })
        .catch((err) => toast(err instanceof Error ? err.message : 'Error taking over', 'Error', 'error'))
    },
    [reload],
  )

  const trashOne = useCallback(
    (ticketId: number) => {
      /* "Are you sure? All ticket data will be lost." - textul sweetalert-ului
         din `TicketQuickActions.deleteTicket`. */
      if (!window.confirm('Are you sure? All ticket data will be lost.')) return
      trashTicket(ticketId)
        .then(() => {
          setExpanded((was) => (was !== null && Number(was.row.ID) === ticketId ? null : was))
          reload()
          toast('Ticket deleted.', 'Success', 'success')
        })
        .catch((err) => toast(err instanceof Error ? err.message : 'Ticket can not be deleted.', 'Error', 'error'))
    },
    [reload],
  )

  const trashChecked = useCallback(() => {
    const ids = [...picked.current].filter((n) => n > 0)
    if (ids.length === 0) return
    if (!window.confirm('Are you sure? All tickets data will be lost.')) return

    trashTickets(ids)
      .then((answer) => {
        setExpanded((was) => (was !== null && ids.includes(Number(was.row.ID)) ? null : was))
        reload()
        toast(
          answer.skipped > 0 ? `${answer.deleted} deleted, ${answer.skipped} skipped.` : 'Tickets deleted.',
          'Success',
          answer.skipped > 0 ? 'warning' : 'success',
        )
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Tickets can not be deleted.', 'Error', 'error'))
  }, [reload])

  /*
   * Butoanele randului sunt HTML pus de coloana (`actionsHtml`), deci nu au
   * cum sa poarte un `onClick` de React. Clicul lor urca insa prin arborele
   * DOM pana aici, exact ca la `$(document).on('click', '.deleteTicket')` din
   * portal - deci ascultam o singura data, pe gazda, in loc sa legam din nou
   * ascultatori dupa fiecare redesenare.
   */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    if (target.matches('input.all-tickets')) {
      const on = (target as HTMLInputElement).checked
      picked.current.clear()
      for (const box of boxes()) {
        box.checked = on
        if (on) picked.current.add(Number(box.dataset.id))
      }
      setChecked(picked.current.size)
      return
    }
    if (target.matches('input.select-ticket')) {
      const box = target as HTMLInputElement
      const ticketId = Number(box.dataset.id)
      if (box.checked) picked.current.add(ticketId)
      else picked.current.delete(ticketId)
      setChecked(picked.current.size)
      return
    }

    const unknown = target.closest<HTMLElement>('.quick-sidebar-unknown')
    if (unknown) {
      event.preventDefault()
      const contactId = Number(unknown.dataset.id)
      if (contactId > 0) setUnknownOn(contactId)
      return
    }

    const button = target.closest<HTMLElement>('.quickTakeOver, .showCommentModal, .deleteTicket')
    if (!button) return

    event.preventDefault()
    const ticketId = Number(button.dataset.id)
    if (!ticketId) return

    if (button.classList.contains('quickTakeOver')) takeOver(ticketId)
    else if (button.classList.contains('showCommentModal')) setCommentOn(ticketId)
    else trashOne(ticketId)
  }

  return (
    <Content>
      <ActiveSavedFilter list={list} activeId={quickId} onClear={() => apply(EMPTY_TICKET_FILTERS, null)} />
      {/* Fereastra se face abia cand se cere, ca in portal: `showCommentModal`
          adauga markupul in `.notifications` la apasare si il scoate la
          inchidere. */}
      {commentOn !== null && (
        <Modal id="commentModal" open onClose={() => setCommentOn(null)} title="New comment" labelledBy="commentModalLabel">
          <TicketCommentForm
            id={commentOn}
            onDone={() => {
              setCommentOn(null)
              reload()
              toast('Comment added.', 'Success', 'success')
            }}
          />
        </Modal>
      )}
      {unknownOn !== null && <UnknownOwnerModal contactId={unknownOn} onClose={() => setUnknownOn(null)} />}
      {creating && (
        <Modal
          id="createTicketModal"
          open
          large
          onClose={() => setCreating(false)}
          title={
            <>
              <i className="fas fa-ticket-alt" aria-hidden="true"></i> New ticket
            </>
          }
          labelledBy="createTicketModalLabel"
        >
          <TicketCreateForm
            onCancel={() => setCreating(false)}
            onDone={(ticket) => {
              setCreating(false)
              setBorn(ticket)
              /* `dataTable.draw(false)`: lista se redeseneaza pe loc. */
              reload()
            }}
          />
        </Modal>
      )}
      {quick && (
        <QuickTicketModal
          onClose={() => setQuick(false)}
          onDone={(ticket) => {
            setQuick(false)
            setBorn(ticket)
            reload()
          }}
        />
      )}
      <div className={chatOn ? 'chat-tickets-table' : undefined}>
      {chatOn && <ChatTicketsTable rows={chat.data?.data ?? []} selected={chatSelected} onSelect={setChatSelected} />}
      <div ref={host} onClick={onClick}>
      <DataTable
        id={id}
        columnTools
        columns={columns}
        rows={rows}
        total={query.data?.recordsTotal ?? 0}
        fetching={query.isFetching}
        error={query.isError ? (query.error as Error).message : null}
        onRetry={refresh}
        state={state}
        onState={setState}
        rowId={(r) => String(r.DT_RowId)}
        emptyHint="No ticket matches these filters."
        /*
         * Butonul `+` de pe prima coloana isi face iar treaba.
         *
         * Coloana era desenata (si trebuie sa ramana - lista noua arata la fel
         * ca cea veche), dar `DataTable` nu primea nimic de deschis, deci
         * clicul cadea in gol.
         *
         * Ce se pune sub rand e ce punea si `expandTicket`: cele doua benzi si
         * cele doua coloane de file, FARA randul de sus. Randul tichetului e
         * chiar randul listei de deasupra, cel pe care s-a apasat `+`; desenat
         * inca o data dedesubt, aducea cu el un al doilea cap de tabel, cu alte
         * coloane decat ale listei. Vezi `TicketExpandedRow`.
         */
        details={(row) => <TicketExpandedRow id={Number(row.ID)} />}
        openRow={expanded?.rid ?? null}
        onOpenRow={openRow}
        lengths={list === 'tickets' ? ACTIVE_LENGTHS : undefined}
        footerButtons={{ copy: true, colvis: true, refresh }}
        headerButtons={
          canCreate ? (
            <>
              <button type="button" className="btn btn-success tk-header-extra new-ticket" onClick={() => setCreating(true)}>
                <i className="fas fa-plus-circle" aria-hidden="true"></i> New ticket
              </button>{' '}
              {/* Al doilea rand al meniului `stretchy-nav-ticket` din portal. */}
              <button type="button" className="btn btn-default tk-header-extra quick-ticket" onClick={() => setQuick(true)}>
                <i className="fas fa-bolt" aria-hidden="true"></i> Quick Ticket
              </button>
            </>
          ) : undefined
        }
        filters={{
          active: activeFilterCount(filters),
          render: (close) => (
            <FiltersPanel
              list={list}
              applied={filters}
              close={close}
              onApply={(f) => {
                apply(f, null)
                close()
              }}
              activeQuickId={quickId}
              onPickSaved={(f) => apply(filtersFromSaved(f), f.ID)}
              onSavedRemoved={(id) => setQuickId((q) => (q === id ? null : q))}
            />
          ),
        }}
      />
      </div>
      </div>
      {chatOn && (
        <div className="ticket-page-chat">
          {chatSelected === null ? (
            <div className="text-center font-12 tk-chat-panel-empty">
              <span className="badge badge-info">Select a chat ticket to start messaging</span>
            </div>
          ) : (
            <ChatSupportPanel id={chatSelected} onRefresh={reload} />
          )}
        </div>
      )}
      {/* Bara "Actions" se deseneaza in body, jos pe mijloc, nu aici: vezi BulkActions. */}
      {list === 'tickets' && (
        <BulkActions
          count={checked}
          onTrash={trashChecked}
          onOpen={(kind) => {
            const ids = [...picked.current].filter((n) => n > 0)
            if (ids.length > 0) setBulk({ kind, ids })
          }}
        />
      )}
      {bulk !== null && (
        <TicketBulkForms
          kind={bulk.kind}
          ids={bulk.ids}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null)
            /* `$datatable.draw(false)`: lista se redeseneaza pe loc. */
            reload()
          }}
        />
      )}
    </Content>
  )
}
